import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Spinner } from '../common/Spinner';
import { Amount } from '../common/Amount';
import { IconChartPie } from '../../icons';
import GrowthBadge from './GrowthBadge';
import { canUseBankReconcile } from '../../config/bankReconcileAccess';
import type { Tier } from '../../config/entitlements';
import { computeYtdDual, type YtdDualResult, type YtdSeries } from '../../utils/cockpitYtd';
import { loadIncomeForUser, loadPaymentsForUser } from '../../loaders/cockpitLoaders';
import { getExpensesForUser } from '../../api/expensesApi';
import logger from '../../utils/logger';
import type { IncomeEntry } from '../../utils/expenseSummary';
import type { Expense } from '../../types/expenseTypes';
import type { Payment } from '../../types/paymentTypes';

/**
 * `cockpit/ResultYtdTile` (E3, M-CFO-3, `architecture-cockpit-cfo.md` §8.3).
 *
 * Zeigt „Ergebnis (laufendes Jahr)" in DOPPEL-BASIS (Entscheidung D3): die
 * rechnungsbasierte Reihe („gestellt", IMMER vorhanden) UND die zahlungsbasierte
 * Reihe („vereinnahmt/gezahlt", fail-closed). Beide Werte stehen ehrlich
 * gelabelt nebeneinander; ein umschaltbarer Tabellen-Fallback listet beide
 * Reihen (A11y §12). YoY (nur auf der robusten accrual-Reihe) erscheint als
 * {@link GrowthBadge}.
 *
 * Datenherkunft (§6/§11): `income`/`expenses` kommen als bereits gebubbelte
 * Props (0 zusätzliche Reads für die accrual-Reihe); NUR `payments` (cash-Reihe)
 * wird kachel-isoliert selbst geladen (+1 Read, `loadPaymentsForUser`). Schlägt
 * der payments-Read fehl, bleibt die accrual-Reihe sichtbar und die cash-Reihe
 * degradiert zum Hinweis + Bank-Abgleich-CTA — NIE ein Kachel-Crash.
 *
 * Jurist-Auflage (J-A4, §8.3): Begriffe „BWA", „Gewinn", „EÜR" sind im UI
 * VERBOTEN. Alle sichtbaren Rechtstexte (Beschreibung, Disclaimer) kommen 1:1
 * aus `cockpit.json` (`cfo.ytd.*`, DE maßgeblich, gesetzt in M-CFO-5).
 *
 * @module components/cockpit/ResultYtdTile
 */

export interface ResultYtdTileProps {
  /** Scope-UID (Owner oder — im Advisor-Kontext — Mandant) für den payments-Read/Retry. */
  scopeUid: string;
  /** Effektiver Tier für das Bank-Abgleich-Gate der Cash-Fallback-CTA (Ultra, §3.2). */
  tier: Tier;
  /** Gebubbelte `IncomeEntry[]` (accrual-Einnahmen); `null` solange nicht geladen. */
  income: IncomeEntry[] | null;
  /** Ladefehler der gebubbelten Einnahmen. */
  incomeError: boolean;
  /** Gebubbelte `Expense[]` (accrual-Ausgaben); `null` solange nicht geladen. */
  expenses: Expense[] | null;
  /** Ladefehler der gebubbelten Ausgaben. */
  expensesError: boolean;
  /** `EXPENSES_ENABLED` — `false` ⇒ Kachel degradiert zu „Einnahmen (laufendes Jahr)". */
  expensesEnabled: boolean;
  /** Heutiges Datum `YYYY-MM-DD` (Parameter für deterministisches Rendern/Tests). */
  todayISO: string;
}

type InputsState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; income: IncomeEntry[]; expenses: Expense[] };

/**
 * Isolierter Ladezustand der eigenen `payments` (cash-Reihe). `payments: null`
 * bedeutet „nicht verfügbar" (Fehler ODER noch nicht geladen) — die cash-Reihe
 * wird dann fail-closed ausgeblendet, ohne die Kachel zu blockieren.
 */
function usePaymentsForCashSeries(scopeUid: string): {
  payments: Payment[] | null;
  settled: boolean;
  retryToken: number;
  reload: () => void;
} {
  const [payments, setPayments] = useState<Payment[] | null>(null);
  const [settled, setSettled] = useState(false);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setSettled(false);
    setPayments(null);
    loadPaymentsForUser(scopeUid)
      .then((data) => {
        if (!cancelled) {
          setPayments(data);
          setSettled(true);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setPayments(null);
          setSettled(true);
        }
        logger.error('ResultYtdTile: payments-Read (cash-Reihe) fehlgeschlagen', err);
      });
    return () => {
      cancelled = true;
    };
  }, [scopeUid, retryToken]);

  return { payments, settled, retryToken, reload: () => setRetryToken((n) => n + 1) };
}

const ResultYtdTile: React.FC<ResultYtdTileProps> = ({
  scopeUid,
  tier,
  income,
  incomeError,
  expenses,
  expensesError,
  expensesEnabled,
  todayISO,
}) => {
  const { t } = useTranslation('cockpit');
  const navigate = useNavigate();
  const [showTable, setShowTable] = useState(false);
  const [manualInputs, setManualInputs] = useState<InputsState | null>(null);
  const { payments, settled: paymentsSettled, reload: reloadPayments } = usePaymentsForCashSeries(scopeUid);

  // accrual-Eingaben: gebubbelte Props, überschreibbar durch einen isolierten
  // Retry (manualInputs) — Muster `RevenueExpenseChartTile`.
  const bubbledError = incomeError || (expensesEnabled && expensesError);
  const bubbledReady = income !== null && (!expensesEnabled || expenses !== null);
  const inputs: InputsState =
    manualInputs ??
    (bubbledError
      ? { status: 'error' }
      : bubbledReady
        ? { status: 'ready', income: income as IncomeEntry[], expenses: expenses ?? [] }
        : { status: 'loading' });

  const retry = async () => {
    setManualInputs({ status: 'loading' });
    reloadPayments();
    try {
      const [inc, exp] = await Promise.all([
        loadIncomeForUser(scopeUid),
        expensesEnabled ? getExpensesForUser(scopeUid) : Promise.resolve([] as Expense[]),
      ]);
      setManualInputs({ status: 'ready', income: inc, expenses: exp });
    } catch (err) {
      setManualInputs({ status: 'error' });
      logger.error('ResultYtdTile: Retry (Ergebnis YTD) fehlgeschlagen', err);
    }
  };

  const navigateToDetail = () => navigate(expensesEnabled ? '/dashboard/ausgaben' : '/dashboard/archiv');

  const title = expensesEnabled ? t('cfo.ytd.title') : t('cfo.ytd.titleIncomeOnly');

  const header = (
    <div className="flex items-center gap-2 mb-2 text-secondary">
      <IconChartPie aria-hidden={true} />
      <h3 className="text-sm font-semibold">{title}</h3>
    </div>
  );

  // ── Fehler-Zustand (accrual nicht berechenbar) ─────────────────────────
  if (inputs.status === 'error') {
    return (
      <Card>
        {header}
        <div className="flex items-center justify-between gap-2">
          <span className="text-secondary">{t('empty.dash')}</span>
          <Button size="sm" variant="secondary" onClick={retry}>
            {t('retry')}
          </Button>
        </div>
      </Card>
    );
  }

  // ── Lade-Zustand (accrual-Eingaben ODER payments noch offen) ───────────
  if (inputs.status === 'loading' || !paymentsSettled) {
    return (
      <Card>
        {header}
        <div className="flex justify-center py-2">
          <Spinner size="sm" />
        </div>
      </Card>
    );
  }

  // ── Bereit: Doppel-Basis berechnen ─────────────────────────────────────
  const result: YtdDualResult = computeYtdDual({
    income: inputs.income,
    expenses: inputs.expenses,
    payments,
    todayISO,
    expensesEnabled,
  });

  const seriesValue = (s: YtdSeries): number => (expensesEnabled ? s.netYtdCents : s.incomeYtdCents);

  const yoyPct = result.yoyDeltaPct;
  const yoyLabel =
    yoyPct !== null
      ? t('cfo.ytd.yoy', { pct: formatSignedPercent(yoyPct) })
      : '';
  const yoyAria =
    yoyPct !== null ? t('cfo.ytd.yoyAria', { pct: formatSignedPercent(yoyPct) }) : '';

  const accrualDescription = expensesEnabled
    ? t('cfo.ytd.accrualDescription')
    : t('cfo.ytd.incomeOnlyDescription');

  // Vereinfachter Translator (key → string) für den Tabellen-Helfer — vermeidet
  // TFunction-Overload-Reibung beim Weiterreichen.
  const tr = (key: string): string => t(key);

  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={navigateToDetail}
      onKeyDown={(e) => e.key === 'Enter' && navigateToDetail()}
      className="cursor-pointer"
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 text-secondary">
          <IconChartPie aria-hidden={true} />
          <h3 className="text-sm font-semibold">{title}</h3>
        </div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setShowTable((v) => !v);
          }}
          className="text-xs text-secondary hover:text-primary underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded"
        >
          {showTable ? t('cfo.ytd.toggleValues') : t('cfo.ytd.toggleTable')}
        </button>
      </div>

      {showTable ? (
        <YtdTable result={result} expensesEnabled={expensesEnabled} t={tr} />
      ) : (
        <div>
          {/* v5 §6.1/§6.2: Kachelkopf gestrafft auf EINE accrual-Headline
              (Amount `size="md"`, Data-Face). Die Doppel-Basis (accrual + cash)
              lebt vollständig im Tabellen-Toggle unten. Bleibt die cash-Reihe
              nicht verfügbar, erscheint hier weiterhin der fail-closed-Hinweis
              (+ Bank-Abgleich-CTA), aber KEINE zweite grosse Zahl. */}
          <p className="text-xs text-secondary">{t('cfo.ytd.basisAccrual')}</p>
          <Amount
            cents={seriesValue(result.accrual)}
            size="md"
            className="font-bold text-primary"
          />
          {result.cash === null && (
            <p className="mt-2 text-sm text-secondary max-w-xs">{t('cfo.ytd.cashUnavailable')}</p>
          )}

          {yoyPct !== null && (
            <div className="mt-2">
              <GrowthBadge pct={yoyPct} label={yoyLabel} ariaLabel={yoyAria} />
            </div>
          )}

          {result.cash === null && canUseBankReconcile(scopeUid, tier) && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate('/dashboard/bank-abgleich');
              }}
              className="mt-2 text-xs text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded"
            >
              {t('cfo.ytd.bankReconcileCta')}
            </button>
          )}

          <p className="mt-3 text-xs text-secondary">{accrualDescription}</p>
          <p className="mt-1 text-xs text-muted">{t('cfo.ytd.disclaimer')}</p>
        </div>
      )}
    </Card>
  );
};

/** Vorzeichenbehaftetes Prozent (z. B. „+18 %" / „−4 %"), locale-neutral integer. */
function formatSignedPercent(pct: number): string {
  const sign = pct > 0 ? '+' : pct < 0 ? '-' : '';
  return `${sign}${Math.abs(pct)} %`;
}

/**
 * Tabellen-Fallback (A11y §12): listet beide Reihen mit Einnahmen/Ausgaben/
 * Überschuss (Spalten Ausgaben/Überschuss nur bei aktivierten Ausgaben). Die
 * cash-Reihe erscheint nur, wenn verfügbar (fail-closed).
 *
 * v5 §6.1 „fit-to-card": kompakte Tabelle (`text-xs`), Beträge rechtsbündig im
 * Data-Face ({@link Amount} `size="sm"`, trägt `tabular-nums`), erste Spalte
 * `truncate`-gedeckelt, Zahlenspalten `whitespace-nowrap`; als LETZTE Instanz
 * ein `overflow-x-auto`-Wrapper um die Tabelle (NIE um die Kachel), damit die
 * Kachel selbst nie horizontal überläuft.
 */
function YtdTable({
  result,
  expensesEnabled,
  t,
}: {
  result: YtdDualResult;
  expensesEnabled: boolean;
  t: (key: string) => string;
}) {
  const rows: Array<{ key: string; basisLabel: string; series: YtdSeries }> = [
    { key: 'accrual', basisLabel: t('cfo.ytd.basisAccrual'), series: result.accrual },
  ];
  if (result.cash !== null) {
    rows.push({ key: 'cash', basisLabel: t('cfo.ytd.basisCash'), series: result.cash });
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-secondary">
            <th className="py-1 pr-3 font-medium">{t('cfo.ytd.colBasis')}</th>
            <th className="py-1 pr-3 font-medium text-right">{t('cfo.ytd.colIncome')}</th>
            {expensesEnabled && (
              <th className="py-1 pr-3 font-medium text-right">{t('cfo.ytd.colExpense')}</th>
            )}
            {expensesEnabled && <th className="py-1 font-medium text-right">{t('cfo.ytd.colNet')}</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="text-primary">
              <td className="py-1 pr-3 max-w-[8rem] truncate" title={r.basisLabel}>
                {r.basisLabel}
              </td>
              <td className="py-1 pr-3 text-right whitespace-nowrap">
                <Amount cents={r.series.incomeYtdCents} size="sm" />
              </td>
              {expensesEnabled && (
                <td className="py-1 pr-3 text-right whitespace-nowrap">
                  <Amount cents={r.series.expenseYtdCents} size="sm" />
                </td>
              )}
              {expensesEnabled && (
                <td className="py-1 text-right whitespace-nowrap">
                  <Amount cents={r.series.netYtdCents} size="sm" />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default ResultYtdTile;
